import React, {useContext} from "react";
import {View, Text} from '@react-pdf/renderer';
import PropTypes from "prop-types";

import {
  getPriceWithCurrency,
  getQuotePriceWithTaxes,
  getTotalPrice,
  toFixedPrice
} from "../../../misc/helpers";
import {DISCOUNT_TYPES} from "../../priceWorksheet/discounstInterface";
import {getSubTotalPrice} from "../../priceWorksheet/discountFunctions";
import {PrintDocumentContext} from "./printDocumentContext";
import PrintDocumentDisclaimer from "./printDocumentDisclaimer";
import {getShowPrice} from "../../../misc/priceFunctions";

const PrintDocumentTotal = (props) => {
  const {styles, discountSections, fabricatorData, locale} = props;
  const fabricatorSettings = fabricatorData.settings;
  const printContext = useContext(PrintDocumentContext);

  if (fabricatorData.isAmericanImport) {
    return null
  }

  let subTotalPrice = getTotalPrice(null, false).materialPrice;
  const totalPrice = getTotalPrice();
  let grandTotal = getQuotePriceWithTaxes(totalPrice, discountSections);

  let allTaxes = totalPrice.priceTaxes.length
    ? totalPrice.priceTaxes
    : [{name: "", materialTax: totalPrice.materialTaxes}];
  const showTaxes = allTaxes.some(tax => tax.materialTax > 0);

  const roundedPrice = (price) => toFixedPrice(Math.trunc(price * 100) / 100);

  const getDiscountPercentageAmount = (discountSections, discountSection, discount) => {
    const currentTotal = getSubTotalPrice(discountSections, discountSection, discount, true);
    const priceTaken = currentTotal * discount.amount / 100 // for ex. 25% from 2700 = 625
    return roundedPrice(priceTaken);
  };

  let discountsTotalAmount = 0;
  if (discountSections.length) {
    discountSections.forEach(discountSection => {
      discountSection.discounts.forEach(discount => {
        if (discount.selected && !discount.isSubTotal && !(discount.isRidged && !discount.specialVisible)) {
          const discountAmount = (discount.type === DISCOUNT_TYPES.price)
            ? discount.amount
            : getDiscountPercentageAmount(discountSections, discountSection, discount);
          discountsTotalAmount = discountsTotalAmount + (+discountAmount);
        }
      })
    })
  }

  subTotalPrice = subTotalPrice.toFixed(2); // always round to 23.65, even if it is 23.00
  grandTotal = grandTotal.toFixed(2); // always round to 23.65, even if it is 23.00

  const showPrice = getShowPrice(fabricatorData, true);

  if (!showPrice) {
    return (
      <View style={styles.footer} wrap={false}>
        <PrintDocumentDisclaimer withTotal={true}
                                 styles={styles}
                                 locale={locale}
                                 discountSections={discountSections}
                                 fabricatorData={fabricatorData}/>
      </View>
    )
  }

  return (
    <View style={styles.footer} wrap={false}>
      <View style={styles.footerLeft}>
        <PrintDocumentDisclaimer withTotal={true}
                                 styles={styles}
                                 locale={locale}
                                 discountSections={discountSections}
                                 fabricatorData={fabricatorData}/>
      </View>
      <View style={styles.total}>
        <View style={styles.totalSectionFirst}>
          <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.subTotal"]}</Text>
          <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, subTotalPrice)}</Text>
        </View>
        {discountsTotalAmount ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.discounts"]}</Text>
            <Text style={styles.totalSectionAmount}>-{getPriceWithCurrency(fabricatorSettings, roundedPrice(discountsTotalAmount))}</Text>
          </View>
        ) : null}
        {showTaxes ? allTaxes.map((taxObject, taxObjectIndex) => {
          const taxTotal = taxObject.materialTax.toFixed(2); // always round to 23.65, even if it is 23.00
          const taxName = taxObject.nameIntl && taxObject.nameIntl[locale]
            ? taxObject.nameIntl[locale]
            : taxObject.state || printContext.messages["receipt.total.tax"]
          return (
            <View style={styles.totalSection} key={taxObjectIndex}>
              <Text style={styles.totalSectionText}>{taxName}</Text>
              <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, taxTotal)}</Text>
            </View>
          )
        }) : null}
        {totalPrice.installationTaxes ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.installationTaxes"]}</Text>
            <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, totalPrice.installationTaxes.toFixed(2))}</Text>
          </View>
        ) : null}
        {fabricatorSettings.useShipping ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["grohe.cart.confirm.columns.delivery"]}</Text>
            <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, totalPrice.totalDeliveryCost.toFixed(2))}</Text>
          </View>
        ) : null}
        {fabricatorSettings.deposit && fabricatorSettings.enableDeposit ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["depositPopup.depositAmount"]}</Text>
            <Text style={styles.totalSectionAmount}>{fabricatorSettings.deposit}%</Text>
          </View>
        ) : null}
        <View style={styles.totalSection}>
          <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.total"]}</Text>
          <Text style={styles.totalSectionAmountFinal}>{getPriceWithCurrency(fabricatorSettings, grandTotal)}</Text>
        </View>
      </View>
    </View>
  )
};

PrintDocumentTotal.propTypes = {
  discountSections: PropTypes.array.isRequired,
  fabricatorData: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

export default PrintDocumentTotal
